import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { DEFAULT_SCROLL_AREA_OVERFLOW, ScrollAreaContext, } from '#pplx/frontend/aether/internal/scroll-area/ScrollAreaContext.js';
import * as ScrollAreaPrimitive from '@radix-ui/react-scroll-area';
import classNames from 'classnames';
import { useCallback, useEffect, useRef, useState } from 'react';
function getScrollAreaMaskImageStyle(fadeEdges, { canScrollUp, canScrollDown }) {
    if (!fadeEdges || (!canScrollUp && !canScrollDown))
        return undefined;
    const topEdge = canScrollUp ? 'transparent 0, #000 16px' : '#000 0';
    const bottomEdge = canScrollDown ? '#000 calc(100% - 16px), transparent 100%' : '#000 100%';
    return `linear-gradient(to bottom, ${topEdge}, ${bottomEdge})`;
}
/**
 * Aether-internal scrollable area component using Radix UI primitives with subtle scrollbar styling.
 *
 * Features:
 * - Scrollbar appears on hover and when scrolling
 * - Subtle styling matching the design system (15% opacity foreground color)
 * - Rounded scrollbar thumb with enhanced hit area
 * - Provides scroll overflow state via `useScrollAreaOverflow()`
 */
export function ScrollArea({ children, maxHeight, className = '', fadeEdges = false, onScroll, orientation = 'vertical', }) {
    const viewportRef = useRef(null);
    const [overflow, setOverflow] = useState(DEFAULT_SCROLL_AREA_OVERFLOW);
    const updateOverflow = useCallback((el) => {
        const canScrollUp = el.scrollTop > 1;
        const canScrollDown = el.scrollTop + el.clientHeight < el.scrollHeight - 1;
        setOverflow((prev) => {
            if (prev.canScrollUp === canScrollUp && prev.canScrollDown === canScrollDown) {
                return prev;
            }
            return { canScrollUp, canScrollDown };
        });
    }, []);
    const handleScroll = useCallback(() => {
        onScroll?.();
        if (viewportRef.current) {
            updateOverflow(viewportRef.current);
        }
    }, [onScroll, updateOverflow]);
    // Captures the viewport ref and computes initial overflow state once the element mounts
    const viewportCallbackRef = useCallback((node) => {
        viewportRef.current = node;
        if (node) {
            updateOverflow(node);
        }
    }, [updateOverflow]);
    // WHY useEffect: ResizeObserver requires imperative DOM subscription to detect
    // content size changes (e.g., items added/removed, search filtering) that affect
    // whether the area is scrollable — no declarative React alternative exists.
    useEffect(() => {
        const viewport = viewportRef.current;
        // Observe the inner content div (Radix wraps children in a div inside the viewport)
        const content = viewport?.firstElementChild;
        if (!viewport || !content) {
            return;
        }
        const observer = new ResizeObserver(() => {
            updateOverflow(viewport);
        });
        observer.observe(content);
        return () => observer.disconnect();
    }, [updateOverflow]);
    return (_jsx(ScrollAreaContext.Provider, { value: overflow, children: _jsxs(ScrollAreaPrimitive.Root, { className: classNames('w-full overflow-hidden group flex min-h-0 flex-col', className), children: [_jsx(ScrollAreaPrimitive.Viewport, { ref: viewportCallbackRef, className: classNames('w-full min-h-0', {
                        // Override Radix's internal display:table div to prevent width expansion flash
                        // See https://github.com/radix-ui/primitives/issues/926
                        '[&>div]:!block [&>div]:w-full [&>div]:!min-w-0': orientation === 'vertical',
                    }), style: {
                        maxHeight,
                        maskImage: getScrollAreaMaskImageStyle(fadeEdges, overflow),
                    }, onScroll: handleScroll, children: children }), _jsx(ScrollAreaPrimitive.Scrollbar, { forceMount: true, orientation: orientation, className: "flex flex-col p-px duration-150 opacity-0 group-hover:opacity-100 data-[state=visible]:opacity-100", children: _jsx(ScrollAreaPrimitive.Thumb, { className: "relative rounded-full duration-quick !w-[5px] bg-[color:rgb(from_var(--fg-primary)_r_g_b_/_0.15)] before:content-[''] before:absolute before:top-1/2 before:left-1/2 before:-translate-x-1/2 before:-translate-y-1/2 before:size-full before:min-w-[10px] before:min-h-[10px]" }) })] }) }));
}
//# sourceMappingURL=ScrollArea.js.map